import { useState } from 'react';
import { PencilIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@Utils';

import { Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle, Label } from '@Components';
import { Input } from '@Components/ui-v2/input';
import { TrackedButton } from '@Components/ui-v2/tracked-button';
import { MasterSourceType } from '@Models';
import ToggleCard from './ToggleCard';

interface MasterSourceSelectProps {
  value: {
    MasterSource: MasterSourceType;
    MasterColumnName?: string;
    DefaultValue?: number;
    Variability?: number;
  };
  onChange: (value: {
    MasterSource: MasterSourceType;
    MasterColumnName?: string;
    DefaultValue?: number;
    Variability?: number;
  }) => void;
  defaultLabel?: string;
  className?: string;
  readOnly?: boolean;
}

const MasterSourceSelect = ({
  value,
  onChange,
  defaultLabel = 'Default Value',
  className,
  readOnly = false,
}: MasterSourceSelectProps) => {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedMaster, setSelectedMaster] = useState<MasterSourceType>(value.MasterSource);
  const [columnName, setColumnName] = useState(value.MasterColumnName || '');

  const handleToggle = (source: MasterSourceType) => {
    if (source === selectedMaster) {
      setSelectedMaster(null);
      setColumnName('');
    } else {
      setSelectedMaster(source);
    }
  };

  const handleSave = () => {
    if (readOnly) return;
    onChange({
      ...value,
      MasterSource: selectedMaster,
      MasterColumnName: selectedMaster ? columnName : undefined,
      DefaultValue: value.DefaultValue !== undefined ? value.DefaultValue : 0,
    });
    setDialogOpen(false);
  };

  const handleColumnNameChange = (newColumnName: string) => {
    setColumnName(newColumnName);
  };

  const handleDefaultValueChange = (defaultValue: number) => {
    onChange({
      ...value,
      DefaultValue: defaultValue,
    });
  };

  const masterOptions = [
    { id: 'ProductMaster', label: 'Product Master' },
    { id: 'LocationMaster', label: 'Location Master' },
    { id: 'ProductLocationMaster', label: 'Product Location Master' },
  ];

  const getMasterLabel = (masterId: MasterSourceType) => {
    const masterOption = masterOptions.find((option) => option.id === masterId);
    return masterOption ? masterOption.label : '';
  };

  const openDialog = () => {
    setSelectedMaster(value.MasterSource);
    setColumnName(value.MasterColumnName || '');
    setDialogOpen(true);
  };

  return (
    <div className={cn('space-y-4', className)}>
      {/* Show selected master and edit button */}
      {value.MasterSource && (
        <div className="flex items-center justify-between p-4 border-solid border-[1px] border-gray-300 rounded-lg bg-white">
          <div>
            <div className="font-medium text-[15px] text-gray-700">{getMasterLabel(value.MasterSource)}</div>
            <div className="text-sm text-gray-500 mt-1">
              {t('MasterSourceSelect.Column')}: {value.MasterColumnName || t('MasterSourceSelect.NotSpecified')}
            </div>
          </div>
          {!readOnly && (
            <TrackedButton
              variant="outline"
              buttonName="OPEN_DIALOG_MASTER_SELECT"
              onClick={openDialog}
              className="flex items-center gap-1"
              isSubmit={false}>
              <PencilIcon size={16} />
              {t('MasterSourceSelect.Edit')}
            </TrackedButton>
          )}
        </div>
      )}

      {/* Default value input */}
      {value.DefaultValue !== undefined && (
        <div className={cn('p-4 rounded-lg bg-white border-solid border-[1px] border-gray-300 mt-2')}>
          <div className="space-y-4">
            <div>
              <div className="flex items-center gap-2">
                <Label htmlFor="default-value">{defaultLabel}</Label>
              </div>
              <div className="flex items-center gap-2">
                <Input
                  id="default-value"
                  type="number"
                  value={value.DefaultValue}
                  onChange={(e) => handleDefaultValueChange(Number(e.target.value))}
                  className={cn('mt-1 bg-white')}
                  readOnly={readOnly}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Master Dialog - Increased size and improved UI */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-xl md:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl">{t('MasterSourceSelect.SelectMasterSource')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-8 py-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {masterOptions.map((option) => (
                <ToggleCard
                  key={option.id}
                  selected={selectedMaster === option.id}
                  onToggle={() => handleToggle(option.id as MasterSourceType)}
                  className="h-full">
                  <h4 className="font-medium">{option.label}</h4>
                  <p className="text-sm text-gray-500 mt-1">Use values from {option.label.toLowerCase()}</p>
                </ToggleCard>
              ))}
            </div>

            {selectedMaster && (
              <div className="p-5 rounded-lg bg-gray-50/50">
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="column-name" className="text-sm font-medium">
                      {t('MasterSourceSelect.ColumnName')}
                    </Label>
                    <Input
                      id="column-name"
                      value={columnName}
                      onChange={(e) => handleColumnNameChange(e.target.value)}
                      placeholder={t('MasterSourceSelect.EnterColumnName')}
                      className="mt-2"
                    />
                    <p className="text-xs text-gray-500 mt-1">{t('MasterSourceSelect.ColumnNameDescription')}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <DialogClose asChild>
              <TrackedButton variant="outline" buttonName="CANCEL_MASTER_SOURCE_SAVING" isSubmit={false}>
                {t('MasterSourceSelect.Cancel')}
              </TrackedButton>
            </DialogClose>
            <TrackedButton variant="outline" buttonName="SAVE_MASTER_SOURCE" onClick={handleSave} isSubmit={false}>
              {t('MasterSourceSelect.SaveChanges')}
            </TrackedButton>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MasterSourceSelect;
